import SEO from '../components/SEO';
import { facebookEventsSchema, organizationSchema, breadcrumbSchema } from '../utils/structuredData';
import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, TrendingUp, Users, Eye, MessageCircle, CheckCircle, ArrowRight } from 'lucide-react';

const FacebookEventsPage = () => {
  return (
    <div style={styles.page}>
      <SEO
        title="Facebook & Instagram Events for Dealerships"
        description="Viral Facebook and Instagram campaigns for dealerships — 100K–400K impressions per event, driving calls, showroom traffic, and online leads. Proven results."
        keywords="Facebook event marketing dealerships, Instagram dealer promotion, social media dealership event, automotive Facebook event, viral dealer promotion, Facebook ads dealership, dealer social media marketing"
        canonicalUrl="https://driven2win.com/services/facebook-events"
        structuredData={[facebookEventsSchema, organizationSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Services',url:'https://driven2win.com/services'},{name:'Facebook Events',url:'https://driven2win.com/services/facebook-events'}])]}
      />
      <style>{`
        @media (max-width: 768px) {
          .subpage-hero-h1 { font-size: 24px !important; letter-spacing: 0.3px !important; word-break: break-word !important; overflow-wrap: break-word !important; hyphens: auto !important; }
          .subpage-hero-sub { font-size: 15px !important; }
          .subpage-hero { padding: 60px 0 !important; }
          .subpage-section-h2 { font-size: 24px !important; }
          .subpage-section-pad { padding: 60px 0 !important; }
          .subpage-cta-h2 { font-size: 24px !important; }
          .subpage-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="subpage-hero-h1">
            POWERFUL FACEBOOK & <span style={{color: '#1A1F3A'}}>INSTAGRAM EVENTS</span>
          </h1>
          <p style={styles.heroSubheadline}>
            Viral Social Campaigns That Generate 100K-400K Impressions and Drive Calls, Traffic, and Sales
          </p>
        </div>
      </section>

      {/* Overview */}
      <section style={styles.overviewSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            AMPLIFY YOUR REACH. <span style={{color: '#FFC300'}}>DOMINATE YOUR MARKET.</span>
          </h2>
          <p style={styles.bodyText}>
            Direct mail reaches a few thousand people. Facebook and Instagram? <strong>Hundreds of thousands.</strong>
          </p>
          <p style={styles.bodyText}>
            Our social media campaigns create MASSIVE AWARENESS for your sales events. We target the right audiences with scroll-stopping content that generates huge engagement, phone calls, and event attendance.
          </p>
          <p style={styles.bodyText}>
            <strong>Result?</strong> 100K-400K impressions per campaign. Thousands of engaged users. Viral reach that puts your event in front of EVERYONE in your market.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.howSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            HOW WE CREATE <span style={{color: '#FFC300'}}>VIRAL BUZZ</span>
          </h2>
          
          <div style={styles.processGrid}>
            <ProcessStep
              icon={<Facebook />}
              title="Facebook Event Pages"
              description="We create professional Facebook Event pages with compelling copy, eye-catching visuals, and clear CTAs that encourage sharing and RSVPs."
            />
            <ProcessStep
              icon={<Users />}
              title="Targeted Ad Campaigns"
              description="Laser-focused ad targeting to qualified buyers in your market — demographics, interests, behaviors, and geographic targeting."
            />
            <ProcessStep
              icon={<Instagram />}
              title="Instagram Stories & Feed"
              description="Scroll-stopping Stories and Feed posts that build excitement and drive engagement. We make your event UNMISSABLE."
            />
            <ProcessStep
              icon={<TrendingUp />}
              title="Engagement Optimization"
              description="Real-time monitoring and optimization. We adjust targeting, creative, and messaging to maximize reach and engagement."
            />
          </div>
        </div>
      </section>

      {/* What We Create */}
      <section style={styles.creativeSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHAT WE <span style={{color: '#FFC300'}}>CREATE</span>
          </h2>
          
          <div style={styles.creativeGrid}>
            <CreativeCard
              title="Event Graphics & Videos"
              description="High-impact visuals designed to stop scrolling. Bold headlines, compelling offers, professional branding that screams 'BIG EVENT!'"
            />
            <CreativeCard
              title="Engagement Content"
              description="Polls, questions, countdowns, and interactive posts that build BUZZ and keep your event top-of-mind."
            />
            <CreativeCard
              title="Urgency Messaging"
              description="Limited-time offers, scarcity messaging, FOMO tactics that drive immediate action. 'This weekend ONLY!' 'First 50 customers!' 'Doors close Sunday!'"
            />
            <CreativeCard
              title="Call-To-Action Posts"
              description="Clear, compelling CTAs that tell viewers EXACTLY what to do next — call now, book appointment, visit event, claim offer."
            />
          </div>
        </div>
      </section>

      {/* Campaign Metrics */}
      <section style={styles.metricsSection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            THE <span style={{color: '#FFFFFF'}}>NUMBERS</span>
          </h2>
          
          <div className="grid-4col" style={styles.metricsGrid}>
            <MetricCard
              icon={<Eye />}
              number="100K-400K"
              label="Impressions Per Campaign"
            />
            <MetricCard
              icon={<Users />}
              number="10K-40K"
              label="People Reached"
            />
            <MetricCard
              icon={<MessageCircle />}
              number="500-2K+"
              label="Engagements"
            />
            <MetricCard
              icon={<TrendingUp />}
              number="150-1000+"
              label="Phone Calls Generated"
            />
          </div>
        </div>
      </section>

      {/* Why It Works */}
      <section style={styles.whySection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHY SOCIAL MEDIA CAMPAIGNS <span style={{color: '#FFC300'}}>CRUSH IT</span>
          </h2>
          
          <div style={styles.whyGrid}>
            <WhyCard
              title="MASSIVE REACH"
              description="Facebook and Instagram have BILLIONS of users. We tap into that audience to put your event in front of hundreds of thousands of people."
            />
            <WhyCard
              title="PRECISE TARGETING"
              description="Unlike billboards or TV ads, we can target EXACTLY who we want — age, location, interests, income, purchase behavior. Zero wasted reach."
            />
            <WhyCard
              title="VIRAL POTENTIAL"
              description="When your content resonates, people SHARE it. One post can reach 10X more people through organic sharing and engagement."
            />
            <WhyCard
              title="TRACKABLE ROI"
              description="Every impression, click, call, and conversion is tracked. You see EXACTLY what you're getting for your investment."
            />
          </div>
        </div>
      </section>

      {/* Platform Benefits */}
      <section style={styles.platformSection}>
        <div style={styles.container}>
          <div style={styles.platformGrid}>
            <PlatformCard
              icon={<Facebook />}
              title="Facebook Events"
              benefits={[
                "Event pages with 5K-20K+ reach",
                "Targeted ads to local buyers",
                "RSVPs that track attendance",
                "Engagement that builds BUZZ",
                "Share potential for viral reach"
              ]}
              color="#1877F2"
            />
            <PlatformCard
              icon={<Instagram />}
              title="Instagram Campaigns"
              benefits={[
                "Stories with swipe-up links",
                "Feed posts with high engagement",
                "Influencer partnerships (optional)",
                "Shopping tags for direct sales",
                "Hashtag campaigns for discovery"
              ]}
              color="#E4405F"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaH2}>
            Ready to <span style={{color: '#1A1F3A'}}>DOMINATE</span> Social Media?
          </h2>
          <p style={styles.ctaText}>
            Let's create a viral campaign that puts your event in front of HUNDREDS OF THOUSANDS of buyers!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary}>
            Get Your FREE Social Media Strategy
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const ProcessStep = ({ icon, title, description }) => (
  <div style={styles.processStep}>
    <div style={styles.processIcon}>
      {React.cloneElement(icon, { size: 48, color: '#FFC300' })}
    </div>
    <h3 style={styles.processTitle}>{title}</h3>
    <p style={styles.processDescription}>{description}</p>
  </div>
);

const CreativeCard = ({ title, description }) => (
  <div style={styles.creativeCard}>
    <h3 style={styles.creativeTitle}>{title}</h3>
    <p style={styles.creativeDescription}>{description}</p>
  </div>
);

const MetricCard = ({ icon, number, label }) => (
  <div style={styles.metricCard}>
    <div style={styles.metricIcon}>
      {React.cloneElement(icon, { size: 48, color: '#FFC300' })}
    </div>
    <div style={styles.metricNumber}>{number}</div>
    <div style={styles.metricLabel}>{label}</div>
  </div>
);

const WhyCard = ({ title, description }) => (
  <div style={styles.whyCard}>
    <h3 style={styles.whyTitle}>{title}</h3>
    <p style={styles.whyDescription}>{description}</p>
  </div>
);

const PlatformCard = ({ icon, title, benefits, color }) => (
  <div style={styles.platformCard}>
    <div style={{...styles.platformIcon, background: color}}>
      {React.cloneElement(icon, { size: 60, color: 'white' })}
    </div>
    <h3 style={styles.platformTitle}>{title}</h3>
    <div style={styles.platformBenefits}>
      {benefits.map((benefit, index) => (
        <div key={index} style={styles.platformBenefit}>
          <CheckCircle size={18} color={color} style={{flexShrink: 0}} />
          <span>{benefit}</span>
        </div>
      ))}
    </div>
  </div>
);

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #FFC300 0%, #FFD700 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(26, 31, 58, 0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#1A1F3A',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '24px',
    color: '#1A1F3A',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.6',
    position: 'relative',
    zIndex: 1,
    fontWeight: '700',
  },
  overviewSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#1A1F3A',
    marginBottom: '32px',
    letterSpacing: '1px',
    textAlign: 'center',
  },
  bodyText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '20px',
    maxWidth: '900px',
    margin: '0 auto 20px',
  },
  howSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  processGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  processStep: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
    border: '3px solid #E5E5E5',
  },
  processIcon: {
    marginBottom: '20px',
  },
  processTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  processDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  creativeSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  creativeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  creativeCard: {
    background: 'linear-gradient(135deg, #FFC300 0%, #FFD700 100%)',
    borderRadius: '16px',
    padding: '40px 32px',
    color: '#1A1F3A',
  },
  creativeTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  creativeDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    fontWeight: '600',
  },
  metricsSection: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    padding: '100px 0',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '32px',
  },
  metricCard: {
    textAlign: 'center',
  },
  metricIcon: {
    marginBottom: '16px',
  },
  metricNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '48px',
    fontWeight: '700',
    color: '#FFC300',
    marginBottom: '8px',
  },
  metricLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  whySection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  whyCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    border: '3px solid #FFC300',
    borderTop: '8px solid #FFC300',
  },
  whyTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#FFC300',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  whyDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  platformSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  platformGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
    gap: '40px',
  },
  platformCard: {
    background: '#F5F5F5',
    borderRadius: '20px',
    padding: '48px 40px',
  },
  platformIcon: {
    width: '100px',
    height: '100px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  platformTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '36px',
    color: '#1A1F3A',
    marginBottom: '24px',
    letterSpacing: '1px',
  },
  platformBenefits: {},
  platformBenefit: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    color: '#333',
    fontWeight: '600',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #FFC300 0%, #FFD700 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#1A1F3A',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#1A1F3A',
    marginBottom: '48px',
    fontWeight: '700',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#1A1F3A',
    color: '#FFC300',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default FacebookEventsPage;
